import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Download,
  Copy,
  Settings2,
  FileSpreadsheet,
  Check,
  ChevronsUpDown,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { ShippingStatusBadge } from './ShippingStatusBadge';
import { DeliveryStatusBadge } from './DeliveryStatusBadge';
import { TrackingTimelineSheet } from './TrackingTimelineSheet';
import { useCanadaPostTracking, usePCTracking } from '../hooks';
import { exportShippingsToCSV, copyShippingsToClipboard } from '../utils/csvExport';
import { exportShippingsToExcel } from '../utils/excelExport';
import type { Shipping, ShippingsFilters, CanadaPostTrackingResult } from '../types';
import {
  getShippingCompanyName,
  isCanadaPost,
  getCanadaPostTrackingUrl,
  SHIPPINGS_PERMISSIONS,
} from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface FilterOption {
  label?: string;
  labelKey?: string;
  value: string;
}

export interface ShippingColumnDef {
  key: string;
  labelKey: string;
  defaultVisible?: boolean;
  sortKey?: ShippingsFilters['sortKey'];
  filterType?: 'text' | 'date-range' | 'select' | 'searchable-select';
  filterOptions?: FilterOption[];
  getValue: (shipping: Shipping) => string | number | null;
  noFilter?: boolean;
  permission?: string;
  headerClassName?: string;
  cellClassName?: string;
  align?: 'left' | 'center' | 'right';
}

const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { labelKey: 'status.to_prepare', value: 'to_prepare' },
  { labelKey: 'status.in_preparation', value: 'in_preparation' },
  { labelKey: 'status.ready', value: 'ready' },
  { labelKey: 'status.verified', value: 'verified' },
  { labelKey: 'status.shipped', value: 'shipped' },
  { labelKey: 'status.received', value: 'received' },
];

const COMPANY_FILTER_OPTIONS: FilterOption[] = [
  { labelKey: 'company.canada_post', value: 'canada_post' },
  { labelKey: 'company.fedex', value: 'fedex' },
  { labelKey: 'company.ups', value: 'ups' },
  { labelKey: 'company.dhl', value: 'dhl' },
  { labelKey: 'company.purolator', value: 'purolator' },
  { labelKey: 'company.planet_courier', value: 'planet_courier' },
  { labelKey: 'company.atek', value: 'atek' },
  { labelKey: 'company.other', value: 'other' },
];

export const COLUMNS: ShippingColumnDef[] = [
  {
    key: 'quoteNumber',
    labelKey: 'table.quoteNumber',
    defaultVisible: true,
    sortKey: 'quoteNumber',
    filterType: 'text',
    getValue: (s) => s.quoteNumber,
    permission: SHIPPINGS_PERMISSIONS.QUOTE,
    headerClassName: 'w-28',
  },
  {
    key: 'accountName',
    labelKey: 'table.accountName',
    defaultVisible: true,
    filterType: 'searchable-select',
    getValue: (s) => s.accountName,
  },
  {
    key: 'slipNumber',
    labelKey: 'table.slipNumber',
    defaultVisible: true,
    sortKey: 'slipNumber',
    filterType: 'text',
    getValue: (s) => s.slipNumber,
    permission: SHIPPINGS_PERMISSIONS.SLIP_NUMBER,
    headerClassName: 'w-24',
    cellClassName: 'font-mono text-sm',
  },
  {
    key: 'company',
    labelKey: 'table.company',
    defaultVisible: true,
    sortKey: 'shippingCompany',
    filterType: 'select',
    filterOptions: COMPANY_FILTER_OPTIONS,
    getValue: (s) => s.shippingCompany,
    permission: SHIPPINGS_PERMISSIONS.SHIPPING_DATA,
  },
  {
    key: 'trackingNumber',
    labelKey: 'table.trackingNumber',
    defaultVisible: true,
    filterType: 'text',
    getValue: (s) => s.trackingNumber,
    permission: SHIPPINGS_PERMISSIONS.SHIPPING_DATA,
    cellClassName: 'font-mono text-sm',
  },
  {
    key: 'shippedDate',
    labelKey: 'table.shippedDate',
    defaultVisible: true,
    sortKey: 'shippedDate',
    noFilter: true,
    getValue: (s) => s.shippedDate,
    permission: SHIPPINGS_PERMISSIONS.SHIPPING_DATA,
  },
  {
    key: 'receivedDate',
    labelKey: 'table.receivedDate',
    defaultVisible: false,
    noFilter: true,
    getValue: (s) => s.receivedDate,
    permission: SHIPPINGS_PERMISSIONS.SHIPPING_DATA,
  },
  {
    key: 'slipStatus',
    labelKey: 'table.slipStatus',
    defaultVisible: true,
    sortKey: 'status',
    filterType: 'select',
    filterOptions: STATUS_FILTER_OPTIONS,
    getValue: (s) => s.status,
    permission: SHIPPINGS_PERMISSIONS.STATUS,
  },
  {
    key: 'deliveryStatus',
    labelKey: 'table.deliveryStatus',
    defaultVisible: true,
    noFilter: true,
    getValue: () => '',
    permission: SHIPPINGS_PERMISSIONS.SHIPPING_DATA,
  },
  {
    key: 'actions',
    labelKey: 'table.actions',
    defaultVisible: true,
    noFilter: true,
    getValue: () => '',
    headerClassName: 'w-[80px]',
  },
];

// =============================================================================
// Column Visibility Persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-shippings-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

export function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

export function saveColumnVisibility(visibility: Record<string, boolean>): void {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: ShippingsFilters['sortKey'];
  currentSortKey?: ShippingsFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: ShippingsFilters['sortKey']) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Props
// =============================================================================

interface ShippingsTableProps {
  shippings: Shipping[];
  pageItemCount: number;
  total: number;
  sortKey?: ShippingsFilters['sortKey'];
  sortDirection?: 1 | -1;
  onSort?: (key: ShippingsFilters['sortKey']) => void;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  isLoading?: boolean;
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>;
}

// =============================================================================
// SearchableSelectFilter (Combobox with search)
// =============================================================================

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try {
      return value ? JSON.parse(value) : [];
    } catch {
      return [];
    }
  }, [value]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-full text-xs justify-between font-normal"
        >
          <span className="truncate">{selectedLabel || '\u2014'}</span>
          {selected.length > 0 ? (
            <X
              className="h-3 w-3 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange(colKey, '');
              }}
            />
          ) : (
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              No results
            </CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className="text-xs"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selected.includes(opt.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Component
// =============================================================================

export function ShippingsTable({
  shippings,
  pageItemCount,
  total,
  sortKey,
  sortDirection = -1,
  onSort,
  page = 0,
  pageSize = 25,
  onPageChange,
  columnFilters,
  onColumnFilterChange,
  isLoading,
  searchableSelectOptions,
}: ShippingsTableProps) {
  const { t, i18n } = useTranslation('shippings');
  const [selectedTrackingPin, setSelectedTrackingPin] = useState<string | null>(null);
  const totalPages = Math.ceil(total / pageSize) || 1;

  // ---------------------------------------------------------------------------
  // Column visibility
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  // ---------------------------------------------------------------------------
  // RBAC permission checks
  // ---------------------------------------------------------------------------
  const { user } = useSession();
  const permissionStrings = user?.permission_strings ?? [];
  const hasAnyShippingsPermission = permissionStrings.some((p) => p.startsWith('shippings.read.'));

  const hasPermission = (perm?: string) => {
    if (!perm) return true;
    return !hasAnyShippingsPermission || permissionStrings.includes(perm);
  };

  const permittedColumns = useMemo(
    () => COLUMNS.filter((col) => hasPermission(col.permission)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionStrings.join(',')],
  );

  const visibleColumns = useMemo(
    () => permittedColumns.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permittedColumns, columnVisibility],
  );

  // ---------------------------------------------------------------------------
  // Canada Post tracking
  // ---------------------------------------------------------------------------
  const canadaPostTrackingNumbers = useMemo(() => {
    return shippings
      .filter((s) => s.trackingNumber && isCanadaPost(s.shippingCompany))
      .map((s) => s.trackingNumber as string);
  }, [shippings]);

  const { data: trackingData, isLoading: isLoadingTracking } = useCanadaPostTracking(
    canadaPostTrackingNumbers,
  );

  const trackingMap = useMemo(() => {
    if (!trackingData) return new Map<string, CanadaPostTrackingResult>();
    const map = new Map<string, CanadaPostTrackingResult>();
    for (const result of trackingData) {
      map.set(result.pin, result);
    }
    return map;
  }, [trackingData]);

  const selectedTracking = selectedTrackingPin
    ? trackingMap.get(selectedTrackingPin) ?? null
    : null;

  // ---------------------------------------------------------------------------
  // Planète Courrier tracking
  // ---------------------------------------------------------------------------
  const pcTrackingNumbers = useMemo(() => {
    return shippings
      .filter((s) => s.trackingNumber && s.shippingCompany === 'planet_courier')
      .map((s) => s.trackingNumber as string);
  }, [shippings]);

  const { data: pcTrackingData, isLoading: isLoadingPCTracking } = usePCTracking(pcTrackingNumbers);

  const pcTrackingMap = useMemo(() => {
    if (!pcTrackingData) return new Map<string, { statusCode: string; statusName: string }>();
    const map = new Map<string, { statusCode: string; statusName: string }>();
    for (const result of pcTrackingData) {
      map.set(result.shipmentNumber, result);
    }
    return map;
  }, [pcTrackingData]);

  // ---------------------------------------------------------------------------
  // Unified delivery status helpers
  // ---------------------------------------------------------------------------
  const getDeliveryStatus = (shipping: Shipping): string | null => {
    if (!shipping.trackingNumber) return null;

    if (isCanadaPost(shipping.shippingCompany)) {
      const cleanedNumber = shipping.trackingNumber.replace(/\s/g, '');
      const result = trackingMap.get(cleanedNumber);
      if (!result?.events?.length) return null;
      return result.events[0].description;
    }

    if (shipping.shippingCompany === 'planet_courier') {
      const pc = pcTrackingMap.get(shipping.trackingNumber);
      return pc?.statusName || null;
    }

    return null;
  };

  const isTrackingLoading = (shipping: Shipping): boolean => {
    if (!shipping.trackingNumber) return false;
    if (isCanadaPost(shipping.shippingCompany)) return isLoadingTracking;
    if (shipping.shippingCompany === 'planet_courier') return isLoadingPCTracking;
    return false;
  };

  // ---------------------------------------------------------------------------
  // "Showing" text
  // ---------------------------------------------------------------------------
  const showingText =
    shippings.length < pageItemCount
      ? t('table.showingOnPage', {
          count: shippings.length,
          pageCount: pageItemCount,
          defaultValue: `Showing ${shippings.length} of ${pageItemCount} on this page`,
        })
      : t('table.showing', {
          count: shippings.length,
          total,
          defaultValue: `Showing ${shippings.length} of ${total}`,
        });

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleExportCSV = () => {
    try {
      exportShippingsToCSV(shippings, 'shippings');
      toast.success(t('toolbar.exportSuccess', { count: shippings.length, defaultValue: `Exported ${shippings.length} shippings to CSV` }));
    } catch {
      toast.error(t('error.export', 'Failed to export shippings'));
    }
  };

  const handleExportExcel = () => {
    try {
      exportShippingsToExcel(shippings);
      toast.success(t('toolbar.exportExcelSuccess', { count: shippings.length, defaultValue: `Exported ${shippings.length} shippings to Excel` }));
    } catch {
      toast.error(t('error.export', 'Failed to export shippings'));
    }
  };

  const handleCopy = async () => {
    try {
      await copyShippingsToClipboard(shippings);
      toast.success(t('toolbar.copySuccess', 'Copied to clipboard'));
    } catch {
      toast.error(t('toolbar.copyError', 'Failed to copy to clipboard'));
    }
  };

  // ---------------------------------------------------------------------------
  // Filter row renderer
  // ---------------------------------------------------------------------------
  const renderColumnFilter = (col: ShippingColumnDef) => {
    if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

    // Searchable-select filter (combobox with search)
    if (col.filterType === 'searchable-select') {
      const options = searchableSelectOptions?.[col.key] || [];
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <SearchableSelectFilter
            colKey={col.key}
            options={options}
            value={columnFilters[col.key] || ''}
            onChange={onColumnFilterChange}
          />
        </TableHead>
      );
    }

    // Multi-select filter
    if (col.filterType === 'select' && col.filterOptions) {
      const selected: string[] = (() => {
        try {
          return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
        } catch {
          return [];
        }
      })();

      const toggle = (opt: string) => {
        const next = selected.includes(opt)
          ? selected.filter((o) => o !== opt)
          : [...selected, opt];
        onColumnFilterChange(col.key, next.length ? JSON.stringify(next) : '');
      };

      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-full text-xs justify-start font-normal"
              >
                {selected.length ? `${selected.length} selected` : '—'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {col.filterOptions.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                >
                  {opt.label ?? t(opt.labelKey!)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }

    // Date range filter
    if (col.filterType === 'date-range') {
      const raw = columnFilters[col.key] || '';
      const [from = '', to = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={from}
              onChange={(e) => onColumnFilterChange(col.key, `${e.target.value}>>${to}`)}
              title="From"
            />
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={to}
              onChange={(e) => onColumnFilterChange(col.key, `${from}>>${e.target.value}`)}
              title="To"
            />
          </div>
        </TableHead>
      );
    }

    // Text filter (default)
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => onColumnFilterChange(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // ---------------------------------------------------------------------------
  // Cell renderer
  // ---------------------------------------------------------------------------
  const renderCell = (col: ShippingColumnDef, shipping: Shipping) => {
    switch (col.key) {
      case 'quoteNumber':
        return (
          <>
            <div className="font-medium">{shipping.quoteNumber || '--'}</div>
            {shipping.quoteProjectName && (
              <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                {shipping.quoteProjectName}
              </div>
            )}
          </>
        );

      case 'accountName':
        if (shipping.accountId && shipping.accountName) {
          return (
            <Link
              to={`/accounts/${shipping.accountId}`}
              className="text-atek-blue hover:text-atek-blue-dark hover:underline truncate block max-w-[200px]"
            >
              {shipping.accountName}
            </Link>
          );
        }
        return shipping.accountName || '—';

      case 'slipNumber':
        return shipping.slipNumber || '—';

      case 'company': {
        const companyInfo = getShippingCompanyName(shipping.shippingCompany);
        return (
          <span className={companyInfo.isUnknown ? 'text-red-500' : ''}>
            {t(`company.${shipping.shippingCompany}`, companyInfo.name)}
          </span>
        );
      }

      case 'trackingNumber':
        return shipping.trackingNumber ? (
          <span title={shipping.trackingNumber}>{shipping.trackingNumber}</span>
        ) : '—';

      case 'shippedDate':
        return shipping.shippedDate
          ? new Date(shipping.shippedDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
          : '—';

      case 'receivedDate':
        return shipping.receivedDate
          ? new Date(shipping.receivedDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
          : '—';

      case 'slipStatus':
        return <ShippingStatusBadge status={shipping.status} />;

      case 'deliveryStatus': {
        const hasTracking = shipping.trackingNumber && (
          isCanadaPost(shipping.shippingCompany) || shipping.shippingCompany === 'planet_courier'
        );
        if (!hasTracking) return <span className="text-muted-foreground">--</span>;

        const deliveryStatus = getDeliveryStatus(shipping);
        const trackingLoading = isTrackingLoading(shipping);

        // Canada Post specific: expected delivery date
        const cleanedNumber = shipping.trackingNumber?.replace(/\s/g, '') ?? null;
        const trackingResult = isCanadaPost(shipping.shippingCompany) && cleanedNumber
          ? trackingMap.get(cleanedNumber)
          : undefined;
        const showExpectedDate =
          trackingResult?.expectedDeliveryDate && !trackingResult?.actualDeliveryDate;

        return (
          <div className="space-y-1">
            <DeliveryStatusBadge status={deliveryStatus} isLoading={trackingLoading} />
            {showExpectedDate && (
              <div className="text-xs text-muted-foreground">
                {t('delivery.expected', 'Expected')}:{' '}
                {new Date(trackingResult!.expectedDeliveryDate + 'T00:00:00').toLocaleDateString(
                  i18n.language === 'fr' ? 'fr-CA' : 'en-CA',
                  { month: 'short', day: 'numeric' },
                )}
              </div>
            )}
          </div>
        );
      }

      case 'actions': {
        const hasTracking = shipping.trackingNumber && isCanadaPost(shipping.shippingCompany);
        if (!hasTracking) return null;

        const cleanedNumber = shipping.trackingNumber?.replace(/\s/g, '') ?? null;
        return (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={() => setSelectedTrackingPin(cleanedNumber)}
            >
              {t('delivery.viewTracking', 'View tracking')}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              asChild
              title={t('delivery.trackOnCanadaPost', 'Track on Canada Post')}
            >
              <a
                href={getCanadaPostTrackingUrl(shipping.trackingNumber!)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        );
      }

      default:
        return String(col.getValue(shipping) ?? '') || '—';
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Export toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <span className="text-sm text-muted-foreground">{showingText}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            {t('toolbar.copyClipboard', 'Copy')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4 mr-1" />
            Excel
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                {t('toolbar.columns', 'Columns')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('table.toggleColumns', 'Toggle columns')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {permittedColumns
                .filter((c) => !c.noFilter)
                .map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                  >
                    {t(col.labelKey)}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {/* Filter row */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col))}
            </TableRow>

            {/* Header row */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.headerClassName, col.align === 'right' && 'text-right')}
                >
                  {col.sortKey && onSort ? (
                    <SortableHeader
                      label={t(col.labelKey)}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection ?? -1}
                      onSort={onSort}
                    />
                  ) : (
                    t(col.labelKey)
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {/* Loading state */}
            {isLoading &&
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {/* Empty state */}
            {!isLoading && shippings.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="py-12 text-center text-muted-foreground"
                >
                  {t('table.noResults', 'No shippings found matching your filters.')}
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {!isLoading &&
              shippings.map((shipping) => (
                <TableRow key={shipping.id} className="hover:bg-muted/50">
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.cellClassName,
                      )}
                    >
                      {renderCell(col, shipping)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Mode B Pagination */}
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('pagination.pageOf', { page: page + 1, total: totalPages })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(page - 1)}
              disabled={page === 0}
            >
              {t('pagination.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= totalPages - 1}
            >
              {t('pagination.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      {/* Tracking Timeline Sheet */}
      <TrackingTimelineSheet
        open={!!selectedTrackingPin}
        onOpenChange={(open) => {
          if (!open) setSelectedTrackingPin(null);
        }}
        tracking={selectedTracking}
      />
    </div>
  );
}
